import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {DeleteProfileCardComponent} from "../../profile-card/delete-profile-card/delete-profile-card.component";
import {PromoCodeResponse} from "../../interface/promoCodeResponse";
import {PromoCodeService} from "../../service/promo-code.service";
import {
  DELETE_PROMO_CODE_DESCRIPTION, DELETE_PROMO_CODE_TITLE_ADMIN,
  DISCOUNT_ERROR_MESSAGE,
  PROMO_CODE_DISCOUNT_MAX_VALUE,
  PROMO_CODE_DISCOUNT_MIN_VALUE,
  PROMO_CODE_LENGTH,
  PROMO_CODE_NAME_PATTERN,
  PROMO_CODE_QTY_MAX_VALUE,
  PROMO_CODE_QTY_MIN_VALUE,
  PROMO_CODE_QTY_PATTERN,
  QTY_ERROR_MESSAGE
} from "../../constants/promo-code.constant";
import {PromoCodeRequest} from "../../interface/promoCodeRequest";
import {discountValidator, quantityValidator} from "../../validator/quantityValidator";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {QrCopyMessageComponent} from "../../qr-copy-message/qr-copy.component";
import {MockupType} from "../../qr-copy-message/mockup-type.enum";
import {PromoCodeType} from "../../enums/promocode-type";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {GetPageUtil} from "../get-page-util";
import {debounceTime, distinctUntilChanged} from "rxjs";
import {PromoCodeUtil} from "../../service/util/promo-code-util.service";

@Component({
  selector: 'app-promoCode',
  templateUrl: './promoCode.component.html',
  styleUrl: './promoCode.component.scss'
})
export class PromoCodeComponent implements OnInit {

  promoCodeGroup: FormGroup | any;
  errorMessage: string | null = null;
  promoCodes: PromoCodeResponse[] = [];
  promoCodePage: number = 0;
  size: number = 9;
  totalPages: number = 0;
  dialogRef: MatDialogRef<DeleteProfileCardComponent> | undefined;
  minDate: string;
  totalCount: number = 0;

  isLoading: boolean = false;
  private getPageUtil: GetPageUtil = new GetPageUtil();
  private isManuallyEntered: boolean = false;

  constructor(
    private promoCodeService: PromoCodeService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    this.minDate = today.toISOString().split('T')[0];
  }

  ngOnInit() {
    this.promoCodeGroup = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(PROMO_CODE_LENGTH),
        Validators.maxLength(PROMO_CODE_LENGTH),
        Validators.pattern(PROMO_CODE_NAME_PATTERN)
      ]),
      quantity: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(100),
        quantityValidator
      ]),
      expiryDate: new FormControl('', [
        Validators.required,
      ]),
      discountPercent: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(100),
        discountValidator
      ]),
      type: new FormControl('', [
        Validators.required
      ]),
    });
    this.subscribeToNameChanges()
    this.getAllPromoCodes()
  }

  getAllPromoCodes() {
    this.promoCodeService.findAll(this.promoCodePage, this.size).subscribe({
      next: (response) => {
        this.promoCodes = response.content;
        this.totalCount = response.totalElements;
        this.totalPages = response.totalPages;
      }
    });
  }


  createPromoCode() {
    if (this.isManuallyEntered || this.promoCodeGroup.valid) {
      const exp: Date = this.promoCodeGroup.get('expiryDate').value;
      exp.setMinutes(exp.getMinutes() - exp.getTimezoneOffset());
      const formattedExpirationDate = exp.toISOString();

      const createPromoCode: PromoCodeRequest = {
        code: this.promoCodeGroup.get('name').value,
        discount: this.promoCodeGroup.get('discountPercent').value,
        expirationDate: formattedExpirationDate,
        quantity: this.promoCodeGroup.get('quantity').value,
        type:this.promoCodeGroup.get('type').value
      };
      this.promoCodeService.create(createPromoCode).subscribe({
        next: (promoCodeResponse: PromoCodeResponse) => {
          this.promoCodes.unshift(promoCodeResponse);
          this.resetForm();
        },
        error: (error) => {
          if (PromoCodeUtil.isPromoCodeDuplicate(error)) {
            this.showSnackbar('Promo code with this name already exists.', SnackBarUtil.CLASSES.error, MockupType.ERROR);
          }
        }
      });
    }
  }

  deletePromoCode(id: bigint) {
    this.dialogRef = this.matDialog.open(DeleteProfileCardComponent, {
      data: {
        deleteTitle: DELETE_PROMO_CODE_TITLE_ADMIN,
        deleteDescription: DELETE_PROMO_CODE_DESCRIPTION,
      }
    });
    this.dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.promoCodeService.deleteById(id).subscribe(() => {
          this.promoCodes = this.promoCodes.filter(promoCode => promoCode.id !== id);
        })
      }
    })
  }

  resetForm() {
    this.promoCodeGroup.get('name').setValue('');
    this.promoCodeGroup.get('quantity').setValue('');
    this.promoCodeGroup.get('expiryDate').setValue('');
    this.promoCodeGroup.get('discountPercent').setValue('');
    this.promoCodeGroup.get('type').setValue('');
    this.promoCodeGroup.markAsPristine();
    this.promoCodeGroup.markAsUntouched();
    this.errorMessage = null;
    this.isManuallyEntered = false;
    this.updatePromoCodeValidation();
  }

  onQuantityInput(event: any): void {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(PROMO_CODE_QTY_PATTERN, '');
    if (inputValue < PROMO_CODE_QTY_MIN_VALUE) {
      this.promoCodeGroup.get('quantity')?.setValue('');
    } else if (inputValue > PROMO_CODE_QTY_MAX_VALUE) {
      this.promoCodeGroup.get('quantity')?.setValue(PROMO_CODE_QTY_MAX_VALUE);
    } else {
      this.promoCodeGroup.get('quantity')?.setValue(inputValue);
    }
  }

  onDiscountInput(event: any): void {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(PROMO_CODE_QTY_PATTERN, '');
    if (inputValue < PROMO_CODE_DISCOUNT_MIN_VALUE) {
      this.promoCodeGroup.get('discountPercent')?.setValue('');
    } else if (inputValue > PROMO_CODE_DISCOUNT_MAX_VALUE) {
      this.promoCodeGroup.get('discountPercent')?.setValue(PROMO_CODE_DISCOUNT_MAX_VALUE);
    } else {
      this.promoCodeGroup.get('discountPercent')?.setValue(inputValue);
    }
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalCount) {
      this.promoCodePage = page;
      this.getAllPromoCodes();
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.promoCodePage, this.totalCount, this.size);
  }

  copyCode(promoCode: PromoCodeResponse): void {
    if (promoCode.quantity == promoCode.quantityLimit) {
      this.showSnackbar('The link you followed has expired.', SnackBarUtil.CLASSES.error, MockupType.ERROR);
    } else {
      navigator.clipboard.writeText(promoCode.code).then(
        () => {
          this.showSnackbar('Promocode successfully copied.', SnackBarUtil.CLASSES.success, MockupType.SUCCESS);
        }
      );
    }
  }

  private showSnackbar(message: string, panelClassNames: string[], type: MockupType): void {
    this.snackBar.openFromComponent(QrCopyMessageComponent, {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: new MockupDataModel(message, type),
    });
  }

  private updatePromoCodeValidation(): void {
    const nameControl = this.promoCodeGroup.get('name');

    if (this.isManuallyEntered) {
      nameControl.clearValidators();
      nameControl.setValidators([Validators.required]);
    } else {
      nameControl.setValidators([
        Validators.required,
        Validators.minLength(PROMO_CODE_LENGTH),
        Validators.maxLength(PROMO_CODE_LENGTH),
        Validators.pattern(PROMO_CODE_NAME_PATTERN)
      ]);
    }
    nameControl.updateValueAndValidity();
  }

  private subscribeToNameChanges(): void {
    this.promoCodeGroup.get('name')!.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
        if (!value) return;

        const isAutoGenerated = PROMO_CODE_NAME_PATTERN.test(value);
        const shouldBeManuallyEntered = !isAutoGenerated;

        if (this.isManuallyEntered !== shouldBeManuallyEntered) {
          this.isManuallyEntered = shouldBeManuallyEntered;
          this.updatePromoCodeValidation();
        }
      });
  }

  protected onAutoGenerationUsed(): void {
    this.isManuallyEntered = false;
    this.updatePromoCodeValidation();
  }

  protected readonly PromoCodeType = PromoCodeType;
  protected readonly QTY_ERROR_MESSAGE = QTY_ERROR_MESSAGE;
  protected readonly DISCOUNT_ERROR_MESSAGE = DISCOUNT_ERROR_MESSAGE;
}
